import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { trackerConnectionSchema, type TrackerConnection } from '@forecastify/shared';
import { Button, Dialog } from '@forecastify/ui';
import { useT } from './i18n';
import { onWorklogCreated, useSession } from './session';
import { workspacePath } from './workspace-path';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 21 Oct 2026 · YOUR HOURS ARE NOT REACHING JIRA, AND YOU ARE TOLD.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A Jira hour goes out under the person's OWN connection (`worklog-out.service.ts`), and without
 * one it waits — `waiting: not_connected`, a quiet badge on a row nobody looks at. The product
 * owner: say so, every time an hour is logged on a synced task, until the connection exists; and
 * mark the profile menu and its *Integrációk* section while it is missing.
 *
 * ⚠️ **"Missing" is a Jira instance this person could connect and has not** —
 * `GET /integrations/connections`, which already lists only providers with a personal connection
 * (YouTrack has none) and only active instances. One with no site yet (discovery has not run) is
 * not offered: there is nothing the person could do about it.
 *
 * ⚠️ **And only for somebody who logs time** (26 Oct 2026). The marks went to everybody in the
 * workspace, the people whose profile says they do not log (`tracks_time`, U-20) included; the
 * server says which (`required`), and for the rest nothing is missing — they can still connect
 * from their account settings, and are not asked to.
 *
 * ⚠️ **"A synced task" is a saved worklog that carries a `syncState`** — `null` means *not ours to
 * sync*. The worklog does not say WHICH instance, so with two Jira instances and one of them
 * connected this can ask once too often; a workspace has one in practice.
 *
 * ⚠️ **Every save point at once.** The session tells this provider about every successful
 * `POST /worklogs` (`onWorklogCreated`), so the timesheet, the calendar, the logs list and the
 * task's own form all raise it without each remembering to.
 */

interface TrackerConnectionState {
  /** The instances this person has not connected; empty when nothing is missing. */
  readonly missing: readonly TrackerConnection[];
  /** The account screen hands its fresh list over after a connect or a disconnect. */
  readonly update: (connections: readonly TrackerConnection[]) => void;
}

const TrackerConnectionContext = createContext<TrackerConnectionState>({
  missing: [],
  update: () => undefined,
});

export function useTrackerConnection(): TrackerConnectionState {
  return useContext(TrackerConnectionContext);
}

const missingOf = (connections: readonly TrackerConnection[]): readonly TrackerConnection[] =>
  connections.filter((one) => one.required && !one.connected && one.remoteSiteId !== null);

export function TrackerConnectionProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const session = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const t = useT();
  const navigate = useNavigate();
  const [missing, setMissing] = useState<readonly TrackerConnection[]>([]);
  const [open, setOpen] = useState(false);
  const missingRef = useRef(missing);
  missingRef.current = missing;

  const workspaceId = session.workspaceId;
  useEffect(() => {
    if (workspaceId === null) {
      setMissing([]);
      return;
    }
    let live = true;
    void sessionRef.current
      .request('/integrations/connections')
      .then(async (response) => {
        /* ⚠️ A 403 is somebody who cannot log time: there is nothing for them to connect. */
        if (!response.ok) return;
        const parsed = trackerConnectionSchema
          .array()
          .safeParse(((await response.json()) as { data?: unknown }).data);
        if (parsed.success && live) setMissing(missingOf(parsed.data));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [workspaceId]);

  /* Every saved hour on a synced task asks again, while the connection is missing. */
  useEffect(
    () =>
      onWorklogCreated((worklog) => {
        if (worklog.syncState !== null && missingRef.current.length > 0) setOpen(true);
      }),
    [],
  );

  const update = useCallback((connections: readonly TrackerConnection[]) => {
    setMissing(missingOf(connections));
  }, []);
  const value = useMemo(() => ({ missing, update }), [missing, update]);

  const slug = session.workspaces.find((one) => one.id === workspaceId)?.slug ?? null;
  const names = missing.map((one) => one.name).join(', ');

  return (
    <TrackerConnectionContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('trackerConnection.title')}
        closeLabel={t('common.close')}
        width={440}
      >
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground text-[13px] leading-relaxed">
            {t('trackerConnection.body', { names })}
          </p>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              {t('common.close')}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                if (slug !== null) {
                  void navigate(workspacePath(slug, '/account?section=integrations'));
                }
              }}
            >
              {t('trackerConnection.cta')}
            </Button>
          </div>
        </div>
      </Dialog>
    </TrackerConnectionContext.Provider>
  );
}
